import { Contact } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import type { ContactValue } from '@/shared/schema';
import { cn, normalizePhoneNumber } from '@/shared/utils';
import { Card, CardContent } from '../../card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../collapsible';
import { FormItem, FormMessage } from '../../form';
import { Input } from '../../input';
import { Label } from '../../label';
import AutoFormLabel from '../common/label';
import type { AutoFormInputComponentProps } from '../types';

interface ContactFieldsProps {
  value: Partial<ContactValue>;
  onFieldChange: (field: keyof Omit<ContactValue, 'fallbackContact'>, newValue: string) => void;
  disabled?: boolean;
  prefix?: string;
  compact?: boolean;
}

const normalizePhoneFieldValue = (value: string): string => normalizePhoneNumber(value);

function ContactFields({
  value,
  onFieldChange,
  disabled,
  prefix = '',
  compact,
}: ContactFieldsProps) {
  const { t } = useIntl();
  const [localName, setLocalName] = useState(value.name ?? '');
  const [localEmail, setLocalEmail] = useState(value.email ?? '');
  const [localPhone1, setLocalPhone1] = useState(value.phone1 ?? '');
  const [localPhone2, setLocalPhone2] = useState(value.phone2 ?? '');

  useEffect(() => {
    setLocalName(value.name ?? '');
  }, [value.name]);

  useEffect(() => {
    setLocalEmail(value.email ?? '');
  }, [value.email]);

  useEffect(() => {
    setLocalPhone1(value.phone1 ?? '');
  }, [value.phone1]);

  useEffect(() => {
    setLocalPhone2(value.phone2 ?? '');
  }, [value.phone2]);

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2', compact ? 'gap-3' : 'gap-4')}>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}name`}>{t('metadata.contact.name')}</Label>
        <Input
          id={`${prefix}name`}
          value={localName}
          onChange={(e) => {
            setLocalName(e.target.value);
            onFieldChange('name', e.target.value);
          }}
          placeholder={t('metadata.contact.namePlaceholder')}
          disabled={disabled}
          className={compact ? 'h-9' : undefined}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}email`}>{t('metadata.contact.email')}</Label>
        <Input
          id={`${prefix}email`}
          type="email"
          value={localEmail}
          onChange={(e) => {
            setLocalEmail(e.target.value);
            onFieldChange('email', e.target.value);
          }}
          placeholder={t('metadata.contact.emailPlaceholder')}
          disabled={disabled}
          className={compact ? 'h-9' : undefined}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}phone1`}>{t('metadata.contact.phone1')}</Label>
        <Input
          id={`${prefix}phone1`}
          type="tel"
          inputMode="numeric"
          value={localPhone1}
          onChange={(e) => {
            const normalizedValue = normalizePhoneFieldValue(e.target.value);
            setLocalPhone1(normalizedValue);
            onFieldChange('phone1', normalizedValue);
          }}
          placeholder={t('metadata.contact.phonePlaceholder')}
          disabled={disabled}
          className={compact ? 'h-9' : undefined}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}phone2`}>{t('metadata.contact.phone2')}</Label>
        <Input
          id={`${prefix}phone2`}
          type="tel"
          inputMode="numeric"
          value={localPhone2}
          onChange={(e) => {
            const normalizedValue = normalizePhoneFieldValue(e.target.value);
            setLocalPhone2(normalizedValue);
            onFieldChange('phone2', normalizedValue);
          }}
          placeholder={t('metadata.contact.phonePlaceholder')}
          disabled={disabled}
          className={compact ? 'h-9' : undefined}
        />
      </div>
    </div>
  );
}

const normalizeContactValue = (value: unknown): Partial<ContactValue> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Partial<ContactValue>;
};

export default function AutoFormContact({
  label,
  isRequired,
  fieldConfigItem,
  field,
  fieldProps,
}: AutoFormInputComponentProps & {
  field: ControllerRenderProps<FieldValues, string>;
}) {
  const { t } = useIntl();
  const { control, setValue, getValues, formState } = useFormContext();
  const { showLabel: _showLabel, disabled } = fieldProps;
  const showLabel = _showLabel === undefined ? true : _showLabel;
  const fieldPath = field.name;

  const watchedContactValue = useWatch({
    control,
    name: fieldPath,
  });
  const contactValue =
    normalizeContactValue(watchedContactValue) ??
    normalizeContactValue(field.value) ??
    normalizeContactValue(getValues(fieldPath)) ??
    {};

  const [isFallbackOpen, setIsFallbackOpen] = useState(() => {
    const fallback = contactValue.fallbackContact;
    if (!fallback) return false;
    return Boolean(fallback.name || fallback.email || fallback.phone1 || fallback.phone2);
  });

  useEffect(() => {
    const fallback = contactValue.fallbackContact;
    if (fallback?.name || fallback?.email || fallback?.phone1 || fallback?.phone2) {
      setIsFallbackOpen(true);
    }
  }, [
    contactValue.fallbackContact?.name,
    contactValue.fallbackContact?.email,
    contactValue.fallbackContact?.phone1,
    contactValue.fallbackContact?.phone2,
  ]);

  const cleanContactValue = useCallback(
    (value: Partial<ContactValue>): Partial<ContactValue> | undefined => {
      const cleaned: Partial<ContactValue> = {};
      if (value.name) cleaned.name = value.name;
      if (value.email) cleaned.email = value.email;
      if (value.phone1) cleaned.phone1 = value.phone1;
      if (value.phone2) cleaned.phone2 = value.phone2;

      if (value.fallbackContact) {
        const cleanedFallback: Partial<Omit<ContactValue, 'fallbackContact'>> = {};
        if (value.fallbackContact.name) cleanedFallback.name = value.fallbackContact.name;
        if (value.fallbackContact.email) cleanedFallback.email = value.fallbackContact.email;
        if (value.fallbackContact.phone1) cleanedFallback.phone1 = value.fallbackContact.phone1;
        if (value.fallbackContact.phone2) cleanedFallback.phone2 = value.fallbackContact.phone2;

        if (Object.keys(cleanedFallback).length > 0) {
          cleaned.fallbackContact = cleanedFallback;
        }
      }

      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    },
    []
  );

  const handleMainFieldChange = useCallback(
    (fieldKey: keyof Omit<ContactValue, 'fallbackContact'>, newValue: string) => {
      const currentValue = normalizeContactValue(getValues(fieldPath)) ?? contactValue;
      const updated = {
        ...currentValue,
        [fieldKey]: newValue || undefined,
      };
      const cleaned = cleanContactValue(updated);

      setValue(fieldPath, cleaned, {
        shouldDirty: true,
        shouldValidate: formState.submitCount > 0,
      });
    },
    [cleanContactValue, contactValue, fieldPath, formState.submitCount, getValues, setValue]
  );

  const handleFallbackFieldChange = useCallback(
    (fieldKey: keyof Omit<ContactValue, 'fallbackContact'>, newValue: string) => {
      const currentValue = normalizeContactValue(getValues(fieldPath)) ?? contactValue;
      const currentFallback = currentValue.fallbackContact ?? {};
      const updatedFallback = {
        ...currentFallback,
        [fieldKey]: newValue || undefined,
      };

      const updated = {
        ...currentValue,
        fallbackContact: updatedFallback,
      };
      const cleaned = cleanContactValue(updated);

      setValue(fieldPath, cleaned, {
        shouldDirty: true,
        shouldValidate: formState.submitCount > 0,
      });
    },
    [cleanContactValue, contactValue, fieldPath, formState.submitCount, getValues, setValue]
  );

  return (
    <FormItem className="col-span-6">
      <Card className="border-border/60">
        <CardContent className="space-y-4">
          {showLabel && (
            <div className="flex items-center gap-2">
              <Contact className="size-4 text-muted-foreground" />
              <AutoFormLabel
                label={fieldConfigItem?.label || label}
                isRequired={isRequired}
                className="text-sm font-medium"
              />
            </div>
          )}
          <ContactFields
            value={contactValue}
            onFieldChange={handleMainFieldChange}
            disabled={disabled}
            prefix="main-"
            compact={true}
          />

          <Collapsible open={isFallbackOpen} onOpenChange={setIsFallbackOpen}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t('metadata.contact.fallbackContact')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('metadata.contact.fallbackDescription')}
                </p>
              </div>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
                >
                  {isFallbackOpen ? t('common.hide') : t('common.show')}
                </button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="mt-3">
              <div className="rounded-lg border bg-muted/20 p-3">
                <ContactFields
                  value={contactValue.fallbackContact ?? {}}
                  onFieldChange={handleFallbackFieldChange}
                  disabled={disabled}
                  prefix="fallback-"
                  compact={true}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          <FormMessage />
        </CardContent>
      </Card>
    </FormItem>
  );
}
